import type { CpvCodeDto } from "../../autogen/bff-api";

export type CpvNode = {
  code: string;
  nameEn: string;
  nameNb: string;
  level: number;
  children: CpvNode[];
};

const CODE_PATTERN = /^\d{8}$/;

function assertValid(code: string): void {
  if (!CODE_PATTERN.test(code)) {
    throw new Error(`Invalid CPV code (must be 8 digits): ${code}`);
  }
}

function lastSignificantPosition(code: string): number {
  for (let i = 7; i >= 0; i--) {
    if (code[i] !== "0") return i;
  }
  return -1;
}

export function levelOf(code: string): number {
  assertValid(code);
  const pos = lastSignificantPosition(code);
  if (pos < 0) {
    throw new Error(`Code has no significant digits: ${code}`);
  }
  return pos === 0 ? 1 : pos;
}

export function parentOf(code: string): string | null {
  assertValid(code);
  const pos = lastSignificantPosition(code);
  if (pos <= 1) return null;
  return code.substring(0, pos) + "0" + code.substring(pos + 1);
}

export function isAncestorOf(ancestor: string, descendant: string): boolean {
  if (ancestor === descendant) return false;
  const ancestorLevel = levelOf(ancestor);
  const descendantLevel = levelOf(descendant);
  if (ancestorLevel >= descendantLevel) return false;
  const prefixLen = ancestorLevel + 1;
  return descendant.substring(0, prefixLen) === ancestor.substring(0, prefixLen);
}

export function indexByCode(codes: CpvCodeDto[]): Record<string, CpvCodeDto> {
  const result: Record<string, CpvCodeDto> = {};
  for (const code of codes) result[code.code] = code;
  return result;
}

export function buildTree(codes: CpvCodeDto[]): CpvNode[] {
  const byCode = new Map<string, CpvNode>();
  for (const c of codes) {
    byCode.set(c.code, {
      code: c.code,
      nameEn: c.nameEn,
      nameNb: c.nameNb,
      level: levelOf(c.code),
      children: [],
    });
  }
  const roots: CpvNode[] = [];
  byCode.forEach((node) => {
    const parentCode = parentOf(node.code);
    const parent = parentCode != null ? byCode.get(parentCode) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  });
  const sortDeep = (nodes: CpvNode[]): void => {
    nodes.sort((a, b) => a.code.localeCompare(b.code));
    for (const n of nodes) sortDeep(n.children);
  };
  sortDeep(roots);
  return roots;
}
